"use client";
import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAppDispatch } from "lib/redux/hooks";
import { setAccessToken } from "lib/redux/uiSlice";
import { googleOneTapSignIn } from "api/googleOneTapApi";
import { getUserProfile } from "api/userApi";
import { initializeGAUserTracking } from "lib/hooks/useGAUserTracking";
import { toast } from "react-toastify";

export default function OneTapTopRight() {
	const router = useRouter();
	const dispatch = useAppDispatch();

	useEffect(() => {
		// Only run on client side
		if (typeof window === "undefined") return;
		
		// Don't show if user is already logged in
		if (localStorage.getItem("access-token")) {
			// console.log("[One Tap] User already logged in, skipping");
			return;
		}

		// Wait for Google Identity Services to load
		const waitForGsi = async () => {
			for (let i = 0; i < 50; i++) {
				// @ts-ignore
				if (window.google?.accounts?.id) return true;
				await new Promise((r) => setTimeout(r, 100));
			}
			return false;
		};

		// Handle One Tap credential callback
		
		const handleCredentialResponse = async (response: any) => {
			const credential = response?.credential;
			if (!credential) {
				// console.error("[One Tap] No credential received");
				return;
			}

			try {
				
				const { accessToken } = await googleOneTapSignIn(credential);

				if (!accessToken) {
					console.error("[One Tap] No access token received from backend");
					return;
				}

				localStorage.setItem("access-token", accessToken);
				dispatch(setAccessToken(accessToken));

				try {
					const userProfile = await getUserProfile();
					if (userProfile && userProfile.id) {
						initializeGAUserTracking(userProfile.id, dispatch);
					}
				} catch (profileError) {
				}

				// Redirect to dashboard or current page
				const currentPage = localStorage.getItem("current-page");
				const redirectPage = currentPage && currentPage !== "/" 
					? currentPage 
					: "/dashboard";

				// Small delay to ensure token is saved properly
				setTimeout(() => {
					router.push(redirectPage);
				}, 100);

			} catch (error: any) {
				// console.error("[One Tap] Authentication failed:", error);
				toast.error("Server Error. Please try again.");
				// Show user-friendly error message
				if (error.response?.status === 401) {
					console.error("[One Tap] Invalid credential - verification failed");
				} else {
					// console.error("[One Tap] Network or server error");
				}
			}
		};

		// Initialize Google One Tap
		const initializeOneTap = async () => {
			const gsiLoaded = await waitForGsi();
			
			if (!gsiLoaded) {
				// console.warn("[One Tap] Google Identity Services not loaded");
				return;
			}

			// @ts-ignore
			const gsi = window.google.accounts.id;
			
			// Initialize with callback
			gsi.initialize({
				client_id: "31384632577-8baskiv1ckophcdnuhaq3d0q35vr7bvp.apps.googleusercontent.com",
				callback: handleCredentialResponse,
				auto_select: false, // Don't auto-select - let user choose
				cancel_on_tap_outside: true, // Dismiss if user clicks outside
			});

			// Show the One Tap prompt
			gsi.prompt((notification: any) => {
				// Handle prompt notifications
				if (notification.isNotDisplayed()) {
					// console.log("[One Tap] Prompt not displayed:", notification.getNotDisplayedReason());
				} else if (notification.isSkippedMoment()) {
					// console.log("[One Tap] Prompt skipped:", notification.getSkippedReason());
				} else if (notification.isDismissedMoment()) {
					// console.log("[One Tap] Prompt dismissed:", notification.getDismissedReason());
				}
			});
		};

		initializeOneTap();
	}, [router, dispatch]);

	return null;
}
